import { z } from "zod";

import { AUTH_CODE_ALPHABET, AUTH_CODE_LENGTH, normalizeAuthCode } from "../constants";
import {
  emailSchema,
  nameSchema,
  passwordSchema,
  redirectPathSchema,
  resourceIdSchema,
  slugSchema,
} from "./common";

export const requestMagicLinkSchema = z.object({
  email: emailSchema,
  redirectTo: redirectPathSchema.optional(),
});

export const credentialsSchema = z.object({ email: emailSchema, password: passwordSchema });

export const signupSchema = credentialsSchema.extend({
  name: nameSchema,
  organizationName: nameSchema,
  organizationSlug: slugSchema,
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export const setPasswordSchema = z.object({
  // Optional: an account created by magic link or Google has no current password to prove.
  currentPassword: z.string().min(1).max(128).optional(),
  newPassword: passwordSchema,
});

/**
 * The emailed one-time code, as typed. Normalised before validation so the spacing someone
 * adds reading it back in groups is the client's problem to display, not the user's to get
 * right.
 *
 * The address travels with it: a code only proves possession of the inbox it was sent to,
 * so verifying it without saying which address would let someone present their own code
 * against somebody else's account.
 */
export const verifyAuthCodeSchema = z.object({
  email: emailSchema,
  code: z
    .string()
    .transform(normalizeAuthCode)
    .pipe(
      z
        .string()
        .length(AUTH_CODE_LENGTH)
        .regex(new RegExp(`^[${AUTH_CODE_ALPHABET}]+$`), "That code should be all digits."),
    ),
  redirectTo: redirectPathSchema.optional(),
});

export const switchWorkspaceSchema = z.object({ organizationId: resourceIdSchema });

export const acceptInvitationSchema = z.object({
  token: z.string().min(20).max(200),
  name: nameSchema.optional(),
  password: passwordSchema.optional(),
});

export type VerifyAuthCodeInput = z.input<typeof verifyAuthCodeSchema>;
export type SignupInput = z.infer<typeof signupSchema>;
export type CredentialsInput = z.infer<typeof credentialsSchema>;
export type RequestMagicLinkInput = z.infer<typeof requestMagicLinkSchema>;

/** The display name shown on lists ("Une liste de Clément"). */
export const updateProfileSchema = z.object({ name: nameSchema });
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** A guest adding an address: first the address, then the code mailed to it. */
export const attachEmailSchema = z.object({ email: emailSchema });
export const verifyAttachEmailSchema = z.object({
  email: emailSchema,
  code: verifyAuthCodeSchema.shape.code,
});
export type AttachEmailInput = z.input<typeof attachEmailSchema>;
export type VerifyAttachEmailInput = z.input<typeof verifyAttachEmailSchema>;
